import { createElement } from 'react';

import {
  type FilterQuery,
  type FilterValueType,
  type ReqsReviewConfig,
  type ReqsReviewConfigItem,
  COUNT_OPERATORS,
  NULL_OPERATORS,
  REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES,
  SELECT_OPERATORS,
  STRING_OPERATORS,
} from '@frontend/api';
import {
  type CascaderSubmenu,
  type IconProps,
  flattenCascaderOptions,
  isCascaderSubmenu,
  DepthIcon,
  DiameterIcon,
  ThruIcon,
  SearchIcon,
  CountersinkIcon,
  NoneCountIcon,
  CounterboreIcon,
  CountIcon,
  CheckmarkIcon,
  FlatnessIcon,
  CylindricityIcon,
  PerpendicularityIcon,
  AngularityIcon,
  TruePositionIcon,
  ConcentricityIcon,
  TotalRunoutIcon,
  CircularRunoutIcon,
  ParallelismIcon,
  SymmetryIcon,
  CircularityIcon,
  StraightnessIcon,
  ProfileLineIcon,
  ProfileSurfaceIcon,
  DnaWarningIcon,
  DollarSignIcon,
  PaperlessCubeIcon,
  LineItemIcon,
  RouterIcon,
  EMPTY_SUBMENU,
  type CascaderOption,
  type CascaderOptionGroup,
  PdfFileIcon,
  ThreadIcon,
  EqualIcon,
  NotEqualIcon,
  GreaterThanIcon,
  GreaterThanOrEqualToIcon,
  LessThanIcon,
  LessThanOrEqualToIcon,
  RangeIcon,
} from '@frontend/components';

import { SignalSelectOptionType } from '../components/SignalSelect';
import { type GroupWithId, type QueryWithId } from './queryBuilding';

type FeaturesIconKeys = 'threads' | 'counterbores' | 'countersinks';
type GeometricCharacteristicIconKeys =
  | 'flatness'
  | 'cylindricity'
  | 'profile_of_line'
  | 'profile_of_surface'
  | 'perpendicularity'
  | 'angularity'
  | 'position'
  | 'concentricity'
  | 'circular_runout'
  | 'total_runout'
  | 'parallelism'
  | 'symmetry'
  | 'circularity'
  | 'straightness';

type IconKey =
  | 'text'
  | 'diameter'
  | 'depth'
  | 'end_type'
  | 'dna'
  | 'file'
  | 'cube'
  | 'line_item'
  | 'dollar'
  | 'router'
  | 'none_count'
  | 'any_count'
  | 'count'
  | 'equal'
  | 'not_equal'
  | 'greater_than'
  | 'greater_than_or_equal_to'
  | 'less_than'
  | 'less_than_or_equal_to'
  | 'range'
  | GeometricCharacteristicIconKeys
  | FeaturesIconKeys;

const ICON_MAP: Record<
  IconKey,
  React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
  >
> = {
  text: SearchIcon,
  diameter: DiameterIcon,
  depth: DepthIcon,
  end_type: ThruIcon,
  threads: ThreadIcon,
  counterbores: CounterboreIcon,
  countersinks: CountersinkIcon,
  none_count: NoneCountIcon,
  any_count: CheckmarkIcon,
  count: CountIcon,
  equal: EqualIcon,
  not_equal: NotEqualIcon,
  greater_than: GreaterThanIcon,
  greater_than_or_equal_to: GreaterThanOrEqualToIcon,
  less_than: LessThanIcon,
  less_than_or_equal_to: LessThanOrEqualToIcon,
  range: RangeIcon,
  flatness: FlatnessIcon,
  cylindricity: CylindricityIcon,
  profile_of_line: ProfileLineIcon,
  profile_of_surface: ProfileSurfaceIcon,
  perpendicularity: PerpendicularityIcon,
  angularity: AngularityIcon,
  position: TruePositionIcon,
  concentricity: ConcentricityIcon,
  circular_runout: CircularRunoutIcon,
  total_runout: TotalRunoutIcon,
  parallelism: ParallelismIcon,
  symmetry: SymmetryIcon,
  circularity: CircularityIcon,
  straightness: StraightnessIcon,
  dna: DnaWarningIcon,
  file: PdfFileIcon,
  cube: PaperlessCubeIcon,
  line_item: LineItemIcon,
  dollar: DollarSignIcon,
  router: RouterIcon,
} as const;

export const ICON_STYLES = {
  color: 'feature.secondary',
  h: 2.5,
  w: 2.5,
  flexShrink: 0,
} as const;

const getIconFromKey = (key?: string | null): React.ReactNode | null => {
  if (!key || !(key in ICON_MAP)) {
    return null;
  }

  const IconComponent = ICON_MAP[key as IconKey];
  return IconComponent ? createElement(IconComponent, ICON_STYLES) : null;
};

// Helpers are added as siblings to filters as a shortcut to pre-fill a query
// with convenient values. We use a prefix in the cascader option to identify them
// so we can append the appropriate sibling options to the cascader
const NUMERIC_HELPER_PREFIX = 'numeric_helper__';
const NUMERIC_HELPERS = {
  EQUALS: `${NUMERIC_HELPER_PREFIX}Equals`,
  NOT_EQUALS: `${NUMERIC_HELPER_PREFIX}Not equals`,
  GREATER_THAN: `${NUMERIC_HELPER_PREFIX}Greater than`,
  GREATER_THAN_OR_EQUAL: `${NUMERIC_HELPER_PREFIX}Greater than or equal`,
  LESS_THAN: `${NUMERIC_HELPER_PREFIX}Less than`,
  LESS_THAN_OR_EQUAL: `${NUMERIC_HELPER_PREFIX}Less than or equal`,
  BETWEEN: `${NUMERIC_HELPER_PREFIX}Between`,
} as const;
const NUMERIC_HELPER_OPTIONS = [
  // Equality operators first
  {
    children: [
      {
        value: NUMERIC_HELPERS.EQUALS,
        label: NUMERIC_HELPERS.EQUALS.replace(NUMERIC_HELPER_PREFIX, ''),
        icon: getIconFromKey('equal'),
      },
      {
        value: NUMERIC_HELPERS.NOT_EQUALS,
        label: NUMERIC_HELPERS.NOT_EQUALS.replace(NUMERIC_HELPER_PREFIX, ''),
        icon: getIconFromKey('not_equal'),
      },
    ],
  },
  // Greater than operators
  {
    children: [
      {
        value: NUMERIC_HELPERS.GREATER_THAN,
        label: NUMERIC_HELPERS.GREATER_THAN.replace(NUMERIC_HELPER_PREFIX, ''),
        icon: getIconFromKey('greater_than'),
      },
      {
        value: NUMERIC_HELPERS.GREATER_THAN_OR_EQUAL,
        label: NUMERIC_HELPERS.GREATER_THAN_OR_EQUAL.replace(
          NUMERIC_HELPER_PREFIX,
          '',
        ),
        icon: getIconFromKey('greater_than_or_equal_to'),
      },
    ],
  },
  // Less than operators
  {
    children: [
      {
        value: NUMERIC_HELPERS.LESS_THAN,
        label: NUMERIC_HELPERS.LESS_THAN.replace(NUMERIC_HELPER_PREFIX, ''),
        icon: getIconFromKey('less_than'),
      },
      {
        value: NUMERIC_HELPERS.LESS_THAN_OR_EQUAL,
        label: NUMERIC_HELPERS.LESS_THAN_OR_EQUAL.replace(
          NUMERIC_HELPER_PREFIX,
          '',
        ),
        icon: getIconFromKey('less_than_or_equal_to'),
      },
    ],
  },
  {
    children: [
      // Range operator
      {
        value: NUMERIC_HELPERS.BETWEEN,
        label: NUMERIC_HELPERS.BETWEEN.replace(NUMERIC_HELPER_PREFIX, ''),
        icon: getIconFromKey('range'),
      },
    ],
  },
];

const SELECT_HELPER_PREFIX = 'select_helper__';
const SELECT_HELPERS = {
  EQUALS: `${SELECT_HELPER_PREFIX}Is`,
} as const;
const SELECT_HELPER_OPTIONS = Object.values(SELECT_HELPERS).map((helper) => ({
  value: helper,
  label: helper.replace(SELECT_HELPER_PREFIX, ''),
}));

const MULTI_SELECT_HELPER_PREFIX = 'multi_select_helper__';
const MULTI_SELECT_HELPERS = {
  IS_IN: `${MULTI_SELECT_HELPER_PREFIX}Is one of`,
  IS_NOT_IN: `${MULTI_SELECT_HELPER_PREFIX}Is not one of`,
} as const;
const MULTI_SELECT_HELPER_OPTIONS = Object.values(MULTI_SELECT_HELPERS).map(
  (helper) => ({
    value: helper,
    label: helper.replace(MULTI_SELECT_HELPER_PREFIX, ''),
  }),
);

const STRING_HELPER_PREFIX = 'string_helper__';
const STRING_HELPERS = {
  REGEX: `${STRING_HELPER_PREFIX}Regex`,
  INCLUDES: `${STRING_HELPER_PREFIX}Includes any of`,
} as const;
const STRING_HELPER_OPTIONS: SignalSelectOptionType[] = [
  {
    value: STRING_HELPERS.INCLUDES,
    label: STRING_HELPERS.INCLUDES.replace(STRING_HELPER_PREFIX, ''),
    description:
      'Match against one or more keywords with optional case-sensitivity.',
  },
  {
    value: STRING_HELPERS.REGEX,
    label: STRING_HELPERS.REGEX.replace(STRING_HELPER_PREFIX, ''),
    description:
      'Match against Regular Expression patterns. Ask your favorite AI model to write these for you.',
  },
] as const;

const BOOLEAN_HELPER_PREFIX = 'boolean_helper__';
const BOOLEAN_HELPERS = {
  TRUE: `${BOOLEAN_HELPER_PREFIX}True`,
  FALSE: `${BOOLEAN_HELPER_PREFIX}False`,
} as const;
const BOOLEAN_HELPER_OPTIONS = Object.values(BOOLEAN_HELPERS).map((helper) => ({
  value: helper,
  label: helper.replace(BOOLEAN_HELPER_PREFIX, ''),
}));

const NULL_HELPER_PREFIX = 'null_helper__';
const NULL_HELPERS = {
  IS_NOT_NULL: `${NULL_HELPER_PREFIX}Is set`,
  IS_NULL: `${NULL_HELPER_PREFIX}Is not set`,
} as const;
const NULL_HELPER_OPTIONS = Object.values(NULL_HELPERS).map((helper) => ({
  value: helper,
  label: helper.replace(NULL_HELPER_PREFIX, ''),
}));

const COUNT_HELPER_PREFIX = 'count_helper__';
const COUNT_HELPERS = {
  COUNT: `${COUNT_HELPER_PREFIX}Count`,
  ANY: `${COUNT_HELPER_PREFIX}Any`,
  NONE: `${COUNT_HELPER_PREFIX}None`,
} as const;
const COUNT_HELPER_OPTIONS = [
  {
    value: COUNT_HELPERS.COUNT,
    label: COUNT_HELPERS.COUNT.replace(COUNT_HELPER_PREFIX, ''),
    icon: getIconFromKey('count'),
    children: NUMERIC_HELPER_OPTIONS,
  },
  {
    value: COUNT_HELPERS.ANY,
    label: COUNT_HELPERS.ANY.replace(COUNT_HELPER_PREFIX, ''),
    icon: getIconFromKey('any_count'),
  },
  {
    value: COUNT_HELPERS.NONE,
    label: COUNT_HELPERS.NONE.replace(COUNT_HELPER_PREFIX, ''),
    icon: getIconFromKey('none_count'),
  },
] as SignalSelectOptionType[];
const COUNT_HELPER_QUERY_MAP = {
  [COUNT_HELPERS.ANY]: {
    value: 0,
    operator: COUNT_OPERATORS.GREATER_THAN,
  },
  [COUNT_HELPERS.NONE]: {
    value: 0,
    operator: COUNT_OPERATORS.EQUALS,
  },
} as const;
export const isCountHelperSubmenuKey = (value: string): boolean =>
  value === COUNT_HELPERS.COUNT;
export const isCountHelperKey = (value: string): boolean =>
  Object.keys(COUNT_HELPER_QUERY_MAP).includes(value);

type HelperQueryMap = Record<string, Pick<FilterQuery, 'value' | 'operator'>>;

const NUMERIC_HELPER_QUERY_MAP: HelperQueryMap = {
  [NUMERIC_HELPERS.GREATER_THAN]: {
    value: 0,
    operator: COUNT_OPERATORS.GREATER_THAN,
  },
  [NUMERIC_HELPERS.LESS_THAN]: {
    value: 0,
    operator: COUNT_OPERATORS.LESS_THAN,
  },
  [NUMERIC_HELPERS.BETWEEN]: {
    value: { min: 0, max: 0 },
    operator: COUNT_OPERATORS.RANGE,
  },
  [NUMERIC_HELPERS.EQUALS]: {
    value: 0,
    operator: COUNT_OPERATORS.EQUALS,
  },
  [NUMERIC_HELPERS.NOT_EQUALS]: {
    value: 0,
    operator: COUNT_OPERATORS.NOT_EQUALS,
  },
  [NUMERIC_HELPERS.GREATER_THAN_OR_EQUAL]: {
    value: 0,
    operator: COUNT_OPERATORS.GREATER_THAN_OR_EQUAL,
  },
  [NUMERIC_HELPERS.LESS_THAN_OR_EQUAL]: {
    value: 0,
    operator: COUNT_OPERATORS.LESS_THAN_OR_EQUAL,
  },
} as const;

const SELECT_HELPER_QUERY_MAP: HelperQueryMap = {
  [SELECT_HELPERS.EQUALS]: {
    value: '',
    operator: COUNT_OPERATORS.EQUALS,
  },
} as const;

const MULTI_SELECT_HELPER_QUERY_MAP: HelperQueryMap = {
  [MULTI_SELECT_HELPERS.IS_IN]: {
    value: [] as string[],
    operator: SELECT_OPERATORS.IS_IN,
  },
  [MULTI_SELECT_HELPERS.IS_NOT_IN]: {
    value: [] as string[],
    operator: SELECT_OPERATORS.NOT_IN,
  },
} as const;

const COMBINED_SELECT_HELPER_QUERY_MAP: HelperQueryMap = {
  ...SELECT_HELPER_QUERY_MAP,
  ...MULTI_SELECT_HELPER_QUERY_MAP,
} as const;

const STRING_HELPER_QUERY_MAP: HelperQueryMap = {
  [STRING_HELPERS.REGEX]: {
    value: '',
    operator: STRING_OPERATORS.REGEX,
  },
  [STRING_HELPERS.INCLUDES]: {
    value: [],
    operator: STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE,
  },
} as const;

const BOOLEAN_HELPER_QUERY_MAP: HelperQueryMap = {
  [BOOLEAN_HELPERS.TRUE]: {
    value: true,
    operator: COUNT_OPERATORS.EQUALS,
  },
  [BOOLEAN_HELPERS.FALSE]: {
    value: false,
    operator: COUNT_OPERATORS.EQUALS,
  },
} as const;

const NULL_HELPER_QUERY_MAP: HelperQueryMap = {
  [NULL_HELPERS.IS_NULL]: {
    value: true,
    operator: NULL_OPERATORS.IS_NULL,
  },
  [NULL_HELPERS.IS_NOT_NULL]: {
    value: false,
    operator: NULL_OPERATORS.IS_NULL,
  },
} as const;

export const HELPER_QUERY_MAP: HelperQueryMap = {
  ...NUMERIC_HELPER_QUERY_MAP,
  ...COMBINED_SELECT_HELPER_QUERY_MAP,
  ...STRING_HELPER_QUERY_MAP,
  ...BOOLEAN_HELPER_QUERY_MAP,
  ...NULL_HELPER_QUERY_MAP,
  ...COUNT_HELPER_QUERY_MAP,
} as const;

/**
 * Configuration for grouping cascader options at specific submenu levels.
 *
 * Each key represents a submenu value, and the value contains groups of option values
 * that should be grouped together in the cascader interface.
 */
const CASCADER_SUBMENU_GROUPING = {
  root: {
    groups: [
      ['GD&T and callouts', 'text.raw_text'],
      ['Geometric analysis'],
      ['files', 'part', 'router', 'line_item', 'quote'],
    ],
  },
};

/**
 * Modifies the grouping of cascader options based on predefined configuration.
 *
 * This function takes a cascader submenu and reorganizes its children into groups
 * based on the CASCADER_SUBMENU_GROUPING configuration. If no grouping config is found
 * for the submenu value, it returns the submenu unchanged.
 *
 * @param submenu - The cascader submenu to modify
 * @returns The modified submenu with reorganized children groups
 */
export const modifyCascaderOptionGrouping = <OptionType extends CascaderOption>(
  submenu: CascaderSubmenu<OptionType>,
): CascaderSubmenu<OptionType> => {
  // Check the submenu value to see if it matches any of the keys in the CASCADER_SUBMENU_GROUPING
  const groupingConfig =
    CASCADER_SUBMENU_GROUPING[
      submenu.value as keyof typeof CASCADER_SUBMENU_GROUPING
    ];

  // Recursively process children first
  const processedChildren = submenu.children.map((group) => ({
    ...group,
    children: group.children.map((option) =>
      isCascaderSubmenu(option) ? modifyCascaderOptionGrouping(option) : option,
    ),
  }));

  // If no grouping config found, return submenu with processed children
  if (!groupingConfig) {
    return {
      ...submenu,
      children: processedChildren,
    };
  }

  // Create a map of option values to their full option objects
  const optionsByValue = new Map(
    processedChildren
      .flatMap((group) => group.children)
      .map((option) => [option.value, option]),
  );

  // Create new grouped children based on the configuration
  const newGroupedChildren = groupingConfig.groups
    .map((groupValues) => {
      // Find options that match the group values
      const groupOptions = groupValues
        .map((value) => optionsByValue.get(value))
        .filter((option): option is OptionType => option !== undefined);

      // Remove matched options from the map to avoid duplicates
      groupValues.forEach((value) => optionsByValue.delete(value));

      return groupOptions.length > 0 ? { children: groupOptions } : null;
    })
    .filter(
      (group): group is CascaderOptionGroup<OptionType> => group !== null,
    );

  // Handle remaining options
  const remainingOptions = Array.from(optionsByValue.values());
  const finalGroupedChildren =
    remainingOptions.length > 0
      ? newGroupedChildren.length > 0
        ? [
            ...newGroupedChildren.slice(0, -1),
            {
              ...newGroupedChildren[newGroupedChildren.length - 1],
              children: [
                ...newGroupedChildren[newGroupedChildren.length - 1].children,
                ...remainingOptions,
              ],
            },
          ]
        : [{ children: remainingOptions }]
      : newGroupedChildren;

  // Return modified submenu with new grouping
  return {
    ...submenu,
    children: finalGroupedChildren,
  };
};

export const getHelperCascaderValueFromOperatorAndValue = (
  configItem: ReqsReviewConfigItem,
  operator: string,
  value?: FilterValueType | null,
): string | undefined => {
  if (configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC) {
    return Object.keys(NUMERIC_HELPER_QUERY_MAP).find((key) => {
      const helperQuery = NUMERIC_HELPER_QUERY_MAP[key];
      return helperQuery.operator === operator;
    });
  } else if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT
  ) {
    // Check for NULL operator first (check both operator and value)
    if (operator === NULL_OPERATORS.IS_NULL) {
      return Object.keys(NULL_HELPER_QUERY_MAP).find((key) => {
        const helperQuery = NULL_HELPER_QUERY_MAP[key];
        return helperQuery.operator === operator && helperQuery.value === value;
      });
    }

    if (configItem.allowMultiSelect) {
      return Object.keys(COMBINED_SELECT_HELPER_QUERY_MAP).find((key) => {
        const helperQuery = COMBINED_SELECT_HELPER_QUERY_MAP[key];
        return helperQuery.operator === operator;
      });
    } else {
      return Object.keys(SELECT_HELPER_QUERY_MAP).find((key) => {
        const helperQuery = SELECT_HELPER_QUERY_MAP[key];
        return helperQuery.operator === operator;
      });
    }
  } else if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING
  ) {
    if (
      operator === STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE ||
      operator === STRING_OPERATORS.INCLUDES
    ) {
      return STRING_HELPERS.INCLUDES;
    }
    return Object.keys(STRING_HELPER_QUERY_MAP).find((key) => {
      const helperQuery = STRING_HELPER_QUERY_MAP[key];
      return helperQuery.operator === operator;
    });
  } else if (
    configItem.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.BOOLEAN
  ) {
    return Object.keys(BOOLEAN_HELPER_QUERY_MAP).find((key) => {
      const helperQuery = BOOLEAN_HELPER_QUERY_MAP[key];
      return helperQuery.operator === operator && helperQuery.value === value;
    });
  }
  return undefined;
};

export const ALL_HELPER_KEYS = new Set<keyof typeof HELPER_QUERY_MAP>([
  ...Object.values(NUMERIC_HELPERS),
  ...Object.values(SELECT_HELPERS),
  ...Object.values(MULTI_SELECT_HELPERS),
  ...Object.values(STRING_HELPERS),
  ...Object.values(BOOLEAN_HELPERS),
  ...Object.values(NULL_HELPERS),
  ...Object.values(COUNT_HELPERS),
]);

export const isHelperKey = (
  value: string,
): value is keyof typeof HELPER_QUERY_MAP =>
  ALL_HELPER_KEYS.has(value as keyof typeof HELPER_QUERY_MAP);

const getQueryHelperCascaderOptions = (
  item: ReqsReviewConfigItem,
): { children: SignalSelectOptionType[] }[] => {
  // For numeric filters, we'll add helper options as siblings
  if (item.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC) {
    return NUMERIC_HELPER_OPTIONS;
  } else if (item.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT) {
    if (item.allowMultiSelect) {
      return [
        {
          children: [
            ...SELECT_HELPER_OPTIONS,
            ...MULTI_SELECT_HELPER_OPTIONS,
            ...NULL_HELPER_OPTIONS,
          ],
        },
      ];
    } else {
      return [{ children: SELECT_HELPER_OPTIONS }];
    }
  } else if (item.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING) {
    return [{ children: STRING_HELPER_OPTIONS }];
  } else if (item.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.BOOLEAN) {
    return [{ children: BOOLEAN_HELPER_OPTIONS }];
  } else if (item.countCompatible) {
    return [{ children: COUNT_HELPER_OPTIONS }];
  }

  return [];
};

/**
 * Transforms a ReqsReviewConfigItem into a SignalSelectOptionType
 * @param item The config item to transform
 * @param existingFieldFullPaths The full paths of existing fields that should be excluded from the options
 * @param constrainToDocumentPath If provided, only options that are children of this document path will be included
 * @returns A SignalSelectOptionType object
 */
const transformConfigItemToOption = (
  item: ReqsReviewConfigItem,
  existingFieldFullPaths: string[],
  constrainToDocumentPath?: string,
): SignalSelectOptionType | null => {
  // If we're constraining to a document path, we only want to include options that are children of that document path
  if (
    constrainToDocumentPath &&
    item.parentDocumentPath !== constrainToDocumentPath
  ) {
    return null;
  }

  const children = item.children
    .map((child) =>
      transformConfigItemToOption(
        child,
        existingFieldFullPaths,
        constrainToDocumentPath,
      ),
    )
    .filter((child) => child !== null) as SignalSelectOptionType[];
  const hasChildren = children.length > 0;
  const icon = getIconFromKey(item.iconKey);

  // if it does not have any filterable children and it is not itself a filterable config,
  // discard the option
  // NOTE: this happens with features and feedback lists for interrogations currently
  if (!hasChildren && !item.queryPath && !item.countCompatible) {
    return null;
  }

  // if the fullPath is already in the existingFieldFullPaths, we will not include helper
  // options, as this means there is already a query for this field, but we still may want
  // to include the option in case the field has children
  if (existingFieldFullPaths.includes(item.fullPath) && !hasChildren) {
    // this means its not actually a meaningful field, so do not include it
    return null;
  } else if (existingFieldFullPaths.includes(item.fullPath) && hasChildren) {
    // this means this is already filtered on, so include children but not helper queries
    return {
      value: item.fullPath,
      label: item.label,
      children: [{ children }],
      icon,
      description: item.description,
      warning: item.warning,
      isOptional: item.isOptional,
    };
  } else {
    const helperOptions = getQueryHelperCascaderOptions(item);
    const hasHelperOptions = helperOptions.length > 0;
    // If we have helpers, we want to include them in a separate group
    return {
      value: item.fullPath,
      label: item.label,
      children: [
        ...(hasChildren ? [{ children }] : []),
        ...(hasHelperOptions ? helperOptions : []),
      ],
      icon,
      description: item.description,
      warning: item.warning,
      isOptional: item.isOptional,
    };
  }
};

/**
 * Creates cascader options from a requirements review configuration
 * @param config The requirements review configuration
 * @param existingFieldFullPaths The full paths of existing fields that should be excluded from the options
 * @param constrainToDocumentPath If provided, only options that are children of this document path will be included
 * @returns A CascaderSubmenu structure
 */
export const createCascaderOptions = ({
  config,
  existingFieldFullPaths = [],
  constrainToDocumentPath,
}: {
  config: ReqsReviewConfig;
  existingFieldFullPaths?: string[];
  constrainToDocumentPath?: string;
}): CascaderSubmenu<SignalSelectOptionType> => {
  const options = config
    .map((item) =>
      transformConfigItemToOption(
        item,
        existingFieldFullPaths,
        constrainToDocumentPath,
      ),
    )
    .filter((item): item is SignalSelectOptionType => item !== null);

  const rootSubmenu = {
    ...EMPTY_SUBMENU,
    children: [
      {
        children: options,
      },
    ],
  };

  return modifyCascaderOptionGrouping(rootSubmenu);
};

/**
 * Compute the cascaderValue as the array of fullPath values from the root of
 * parentConfig.children down to the fullPath of this query
 *
 * This is used to determine the cascader value for a query in the query builder
 *
 * @param options The cascader submenu options to search through
 * @param targetFullPath The full path to find
 * @param path The current path
 * @returns The full path chain, or null if the target full path is not found
 */
export const findFullPathChain = (
  options: CascaderSubmenu<SignalSelectOptionType>,
  targetFullPath: string,
  path: string[] = [],
): string[] | null => {
  const flatOptions = flattenCascaderOptions(
    options.children.flatMap((group) => group.children),
  );

  for (const node of flatOptions) {
    const newPath = [...path, node.value as string];
    if (node.value === targetFullPath) {
      return newPath;
    }
    if (isCascaderSubmenu(node) && node.children.length > 0) {
      const result = findFullPathChain(
        {
          label: node.label,
          value: node.value,
          children: node.children,
        },
        targetFullPath,
        newPath,
      );
      if (result) {
        return result;
      }
    }
  }
  return null;
};

type QueryBuilderCascaderInfo = {
  cascaderOptions: CascaderSubmenu<SignalSelectOptionType>;
  cascaderValue: string[];
};

/**
 * Get the cascader info for a query in the query builder. Includes the cascader options and
 * the current value for the cascader based on the query information.
 *
 * This works by first finding the full path chain from the root of the config to the query,
 * and then using this to find the config object corresponding to the query path. We then
 * construct cascader options that are siblings of the query (in the same documentPath / group
 * space). Then to find the cascader value, which will be the array of all fullPath values from
 * the documentPath config item down to the full query path.
 *
 * @param group The group the query belongs to
 * @param query The query to get the cascader info for
 * @param configMap The config map
 * @returns The cascader info, or null if the query is not found
 */
export const getQueryBuilderCascaderInfo = ({
  group,
  query,
  configMap,
}: {
  group: GroupWithId;
  query: QueryWithId;
  configMap: Record<string, ReqsReviewConfigItem>;
}): QueryBuilderCascaderInfo | null => {
  const fullPath = [group.documentPath, ...query.fieldName].join('.');
  const queryConfig = configMap[fullPath];
  if (!queryConfig) {
    console.error(`Config not found for fullPath ${fullPath}`);
    return null;
  }

  const parentConfig = configMap[queryConfig.parentDocumentPath];
  if (!parentConfig) {
    console.error(
      `Parent config not found for fullPath ${queryConfig.parentDocumentPath}`,
    );
    return null;
  }

  // Create cascader options for this specific query
  // exclude fields already set but keep the current field included
  const siblingConfigs =
    parentConfig.children.length > 0 ? parentConfig.children : [parentConfig];
  const existingFieldFullPaths = group.queries
    .map((query) => [group.documentPath, ...query.fieldName].join('.'))
    .filter((childFullPath) => childFullPath !== fullPath);

  const cascaderOptions = createCascaderOptions({
    config: siblingConfigs,
    existingFieldFullPaths,
    constrainToDocumentPath: group.documentPath,
  });

  const cascaderFullPathChain = findFullPathChain(cascaderOptions, fullPath);

  if (!cascaderFullPathChain) {
    console.error(`Full path chain not found for fullPath ${fullPath}`);
    return null;
  }

  const helperKey = getHelperCascaderValueFromOperatorAndValue(
    queryConfig,
    query.operator,
    query.value,
  );

  // now we need to concatenate the helper key if we found one
  const cascaderValue = helperKey
    ? [...cascaderFullPathChain, helperKey]
    : cascaderFullPathChain;

  return {
    cascaderOptions,
    cascaderValue,
  };
};

/**
 * Get the cascader info for adding a query to a group in the query builder.
 *
 * Gets cascader options that exclude the already configured fields in the group.
 * Gets the value for the group cascader by finding full path within parent cascader
 * options.
 *
 * @param group The group to add the query to
 * @param configMap The config map
 * @param parentCascaderOptions The cascader options for top level group cascader
 * @returns The cascader info, or null if the group is not found
 */
export const getAddQueryToGroupCascaderInfo = ({
  group,
  configMap,
  parentCascaderOptions,
}: {
  group: GroupWithId;
  configMap: Record<string, ReqsReviewConfigItem>;
  parentCascaderOptions: CascaderSubmenu<SignalSelectOptionType>;
}): QueryBuilderCascaderInfo | null => {
  const groupConfig = configMap[group.documentPath];
  if (!groupConfig) {
    console.error(`Group config not found for fullPath ${group.documentPath}`);
    return null;
  }

  const siblingConfigs =
    groupConfig.children.length > 0 ? groupConfig.children : [groupConfig];
  const existingFieldFullPaths = group.queries.map((query) =>
    [group.documentPath, ...query.fieldName].join('.'),
  );

  const cascaderValue = findFullPathChain(
    parentCascaderOptions,
    groupConfig.fullPath,
  );
  if (!cascaderValue) {
    console.error(
      `Full path chain not found for fullPath ${groupConfig.fullPath}`,
    );
    return null;
  }

  return {
    cascaderOptions: createCascaderOptions({
      config: siblingConfigs,
      existingFieldFullPaths,
      constrainToDocumentPath: group.documentPath,
    }),
    cascaderValue,
  };
};
